'use client';
import React, {
  PropsWithChildren,
  useContext,
  useState,
  KeyboardEvent,
  Dispatch,
  SetStateAction,
} from 'react';
import { useLocalStorage } from '@osmosis/shared/hooks';

import { SHORTCUTS_QUIZ_DEFAULT } from '../constants/quizShortcuts';

export type SetShortcuts = ReturnType<
  typeof useLocalStorage<typeof SHORTCUTS_QUIZ_DEFAULT>
>[1];

export type Shortcuts = {
  [key in keyof typeof SHORTCUTS_QUIZ_DEFAULT]: KeyboardEvent<HTMLButtonElement>['key'];
};

export type QuizShortcutsContextProps = {
  showKeyboardShortcuts: boolean;
  setShowKeyboardShortcuts: (val: boolean) => void;
  shortcuts: typeof SHORTCUTS_QUIZ_DEFAULT;
  setShortcuts: SetShortcuts;
  shortcutActiveEditID: string | null;
  setShortcutActiveEditID: Dispatch<SetStateAction<string | null>>;
  shortcutsEnabled: boolean;
  setShortcutsEnabled: (val: boolean) => void;
};

export const QuizShortcutsContext =
  React.createContext<QuizShortcutsContextProps>({
    showKeyboardShortcuts: false,
    /**
     * The QuizProvider contains all state shared across a quiz.
     */
    setShowKeyboardShortcuts: () => null,
    shortcuts: SHORTCUTS_QUIZ_DEFAULT,
    /**
     * Setter for changing shourcuts in state and localStorage.
     */
    setShortcuts: () => null,
    shortcutActiveEditID: null,
    setShortcutActiveEditID: () => null,
    shortcutsEnabled: true,
    setShortcutsEnabled: () => null,
  });

/**
 * The QuizProvider contains all state shared across a quiz.
 */
export const QuizShortcutsProvider = (
  props: PropsWithChildren,
): JSX.Element => {
  const { children } = props;

  const [shortcuts, setShortcuts] = useLocalStorage(
    'shortcuts',
    SHORTCUTS_QUIZ_DEFAULT,
  );
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  const [shortcutActiveEditID, setShortcutActiveEditID] = useState<
    string | null
  >(null);
  const [shortcutsEnabled, setShortcutsEnabled] = useLocalStorage(
    'shortcutsEnabled',
    true,
  );

  return (
    <QuizShortcutsContext.Provider
      value={{
        showKeyboardShortcuts,
        setShowKeyboardShortcuts,
        shortcuts,
        setShortcuts,
        shortcutActiveEditID,
        setShortcutActiveEditID,
        shortcutsEnabled,
        setShortcutsEnabled,
      }}
    >
      {children}
    </QuizShortcutsContext.Provider>
  );
};

/**
 * Retrieves the current QuizContext.
 */
export const useQuizShortcutsContext = (): QuizShortcutsContextProps =>
  useContext(QuizShortcutsContext);
